import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Building2, Calculator, FileText, Users, TrendingUp, Loader2, Send, Clock,
  CheckCircle, XCircle, CreditCard, Landmark, Receipt, ArrowRight
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BANK_DETAILS = {
  bank: "FCMB",
  accountNumber: "3550551010",
  accountName: "Blue Element Nigeria Ltd"
};

const PAYMENT_TERMS_OPTIONS = [
  { value: "full", label: "Full Payment", desc: "Pay 100% upfront" },
  { value: "two_installments", label: "Two Installments (50/50)", desc: "Pay 50% now, 50% on completion" },
  { value: "eighty_twenty", label: "Two Installments (80/20)", desc: "Pay 80% now, 20% on completion" }
];

const ServicesPage = () => {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [requestForm, setRequestForm] = useState({ asset_id: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  // Quote acceptance flow
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptingRequest, setAcceptingRequest] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState("full");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [decliningRequest, setDecliningRequest] = useState(null);

  // Invoice / payment
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceRequest, setInvoiceRequest] = useState(null);
  const [paying, setPaying] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, assetsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services/requests`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setServices(servicesRes.data);
      setAssets(assetsRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Verify payment on redirect
  useEffect(() => {
    const reference = searchParams.get("reference") || searchParams.get("trxref");
    if (reference && token) {
      verifyServicePayment(reference);
    }
  }, [searchParams, token]);

  const verifyServicePayment = async (reference) => {
    try {
      // Find which request this payment belongs to
      for (const req of requests) {
        if (req.invoice) {
          const res = await axios.post(
            `${API}/services/requests/${req.request_id}/verify-payment?reference=${reference}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.all_paid) {
            toast.success("All payments complete!");
          } else {
            toast.success("Payment verified successfully");
          }
          fetchData();
          return;
        }
      }
    } catch (err) {
      // Payment verification may not match any service request - ignore silently
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount / 100);
  };

  const formatNaira = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };

  const getServiceIcon = (iconName) => {
    const icons = { building: Building2, calculator: Calculator, 'file-text': FileText, users: Users, 'trending-up': TrendingUp };
    return icons[iconName] || Building2;
  };

  const handleRequestService = (service) => {
    if (user?.subscription_plan === "free") {
      toast.error("Marketplace access requires Professional or Enterprise plan");
      return;
    }
    setSelectedService(service);
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.asset_id) { toast.error("Please select an asset"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/services/request`, {
        asset_id: requestForm.asset_id,
        service_type: selectedService.service_id,
        description: requestForm.description
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Service request submitted!");
      setShowRequestModal(false);
      setRequestForm({ asset_id: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const openAcceptFlow = (req) => {
    setAcceptingRequest(req);
    setPaymentTerms("full");
    setPaymentMethod("");
    setShowAcceptModal(true);
  };

  const handleAcceptQuote = async () => {
    if (!paymentMethod) { toast.error("Please select a payment method"); return; }
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/services/requests/${acceptingRequest.request_id}/accept`, {
        payment_terms: paymentTerms,
        payment_method: paymentMethod
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Quote accepted! Invoice generated.");
      setShowAcceptModal(false);
      fetchData();
      // Show invoice immediately
      const updatedReq = { ...acceptingRequest, status: "accepted", invoice: res.data.invoice, payment_method: paymentMethod };
      setInvoiceRequest(updatedReq);
      setShowInvoiceModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept quote");
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineQuote = async () => {
    try {
      await axios.post(`${API}/services/requests/${decliningRequest.request_id}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote declined");
      setShowDeclineDialog(false);
      setDecliningRequest(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to decline quote");
    }
  };

  const handlePayInstallment = async (requestId, installmentIndex) => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/services/requests/${requestId}/pay?installment_index=${installmentIndex}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize payment");
      setPaying(false);
    }
  };

  const openInvoice = (req) => {
    setInvoiceRequest(req);
    setShowInvoiceModal(true);
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: "status-pending",
      quoted: "bg-violet-500/10 text-violet-400 border-violet-500/30",
      accepted: "bg-primary/10 text-primary border-primary/30",
      paid: "status-complete",
      completed: "status-complete",
      declined: "bg-red-500/10 text-red-400 border-red-500/30",
      in_progress: "bg-amber-500/10 text-amber-400 border-amber-500/30"
    };
    return map[status] || "status-pending";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Service Marketplace</h1>
          <p className="text-slate-400 mt-1">Request professional services for your assets</p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = getServiceIcon(service.icon);
            return (
              <Card key={service.service_id} className="card-dashboard group" data-testid={`service-card-${service.service_id}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-slate-400 text-sm mb-4">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-white mono">{formatCurrency(service.base_price)}</p>
                    <Button size="sm" className="btn-primary" onClick={() => handleRequestService(service)}
                      disabled={user?.subscription_plan === "free"} data-testid={`request-${service.service_id}-btn`}>
                      Request
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* My Requests */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white">My Service Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-8">
                <Send className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No service requests yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <div key={request.request_id} className="p-5 rounded-xl bg-slate-800/50 border border-slate-700" data-testid={`request-item-${request.request_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium capitalize">{request.service_type.replace(/_/g, ' ')}</h4>
                        <p className="text-slate-400 text-sm">{new Date(request.created_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={getStatusBadge(request.status)} data-testid={`request-status-${request.request_id}`}>
                        {request.status}
                      </Badge>
                    </div>
                    {request.description && <p className="text-slate-400 text-sm mb-3">{request.description}</p>}

                    {/* Quote with Accept/Decline */}
                    {request.quote && !request.invoice && request.status !== "declined" && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-slate-500 text-xs">Quote</p>
                            <p className="text-white font-bold mono text-lg">{formatNaira(request.quote)}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openAcceptFlow(request)} data-testid={`accept-quote-${request.request_id}`}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Accept
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                              onClick={() => { setDecliningRequest(request); setShowDeclineDialog(true); }}
                              data-testid={`decline-quote-${request.request_id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Decline
                            </Button>
                          </div>
                        </div>
                        {request.admin_notes && <p className="text-slate-400 text-sm mt-2 italic">Admin note: {request.admin_notes}</p>}
                      </div>
                    )}

                    {/* Invoice / Payment Status */}
                    {request.invoice && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-slate-500 text-xs">Invoice {request.invoice.invoice_id}</p>
                            <p className="text-white font-bold mono">{formatNaira(request.invoice.total)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {request.invoice.installments.some(i => i.status === "pending") && (
                              <Button size="sm" className="btn-primary" onClick={() => openInvoice(request)} data-testid={`view-invoice-${request.request_id}`}>
                                <Receipt className="w-4 h-4 mr-1" /> View Invoice
                              </Button>
                            )}
                            {request.invoice.installments.every(i => i.status === "paid") && (
                              <Badge className="status-complete"><CheckCircle className="w-3 h-3 mr-1" /> Fully Paid</Badge>
                            )}
                          </div>
                        </div>
                        {/* Installment progress */}
                        <div className="mt-2 space-y-1">
                          {request.invoice.installments.map((inst, idx) => (
                            <div key={`progress-${request.request_id}-${idx}`} className="flex items-center justify-between text-sm">
                              <span className="text-slate-400">{inst.label}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-white mono">{formatNaira(inst.amount)}</span>
                                <Badge className={inst.status === "paid" ? "status-complete" : "status-pending"} >
                                  {inst.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Declined */}
                    {request.status === "declined" && request.quote && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-red-400 text-sm">Quote of {formatNaira(request.quote)} was declined</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Request Service Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Request Service</DialogTitle>
              <DialogDescription className="text-slate-400">
                {selectedService?.name} - {selectedService && formatCurrency(selectedService.base_price)} starting
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Select Asset</Label>
                <Select value={requestForm.asset_id} onValueChange={(value) => setRequestForm({ ...requestForm, asset_id: value })}>
                  <SelectTrigger className="input-default" data-testid="service-asset-select"><SelectValue placeholder="Choose an asset" /></SelectTrigger>
                  <SelectContent>
                    {assets.map((asset) => (
                      <SelectItem key={asset.asset_id} value={asset.asset_id}>{asset.name} - {asset.state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Additional Details (Optional)</Label>
                <Textarea placeholder="Describe your specific requirements..." value={requestForm.description}
                  onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                  className="input-default min-h-[100px]" data-testid="service-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestModal(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleSubmitRequest} className="btn-primary" disabled={submitting} data-testid="submit-service-request-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : "Submit Request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Accept Quote Modal — Payment Terms & Method */}
        <Dialog open={showAcceptModal} onOpenChange={setShowAcceptModal}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Accept Quote</DialogTitle>
              <DialogDescription className="text-slate-400">
                {acceptingRequest?.service_type.replace(/_/g, ' ')} — {acceptingRequest && formatNaira(acceptingRequest.quote)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              {/* Payment Terms */}
              <div className="space-y-3">
                <Label className="text-slate-300 text-sm font-medium">Payment Terms</Label>
                {PAYMENT_TERMS_OPTIONS.map((opt) => {
                  const isSelected = paymentTerms === opt.value;
                  let breakdown = "";
                  if (acceptingRequest) {
                    const q = acceptingRequest.quote;
                    if (opt.value === "full") breakdown = formatNaira(q);
                    else if (opt.value === "two_installments") breakdown = `${formatNaira(q/2)} + ${formatNaira(q/2)}`;
                    else breakdown = `${formatNaira(q*0.8)} + ${formatNaira(q*0.2)}`;
                  }
                  return (
                    <div key={opt.value}
                      onClick={() => setPaymentTerms(opt.value)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-700 hover:border-slate-600'}`}
                      data-testid={`terms-${opt.value}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-primary' : 'text-white'}`}>{opt.label}</p>
                          <p className="text-slate-400 text-sm">{opt.desc}</p>
                        </div>
                        <p className="text-white mono text-sm">{breakdown}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <Label className="text-slate-300 text-sm font-medium">Payment Method</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div onClick={() => setPaymentMethod("paystack")}
                    className={`p-4 rounded-xl border cursor-pointer text-center transition-all ${paymentMethod === 'paystack' ? 'border-primary bg-primary/5' : 'border-slate-700 hover:border-slate-600'}`}
                    data-testid="method-paystack">
                    <CreditCard className={`w-8 h-8 mx-auto mb-2 ${paymentMethod === 'paystack' ? 'text-primary' : 'text-slate-400'}`} />
                    <p className={`font-medium ${paymentMethod === 'paystack' ? 'text-primary' : 'text-white'}`}>Pay with Paystack</p>
                    <p className="text-slate-500 text-xs mt-1">Card / Bank Transfer</p>
                  </div>
                  <div onClick={() => setPaymentMethod("bank_transfer")}
                    className={`p-4 rounded-xl border cursor-pointer text-center transition-all ${paymentMethod === 'bank_transfer' ? 'border-primary bg-primary/5' : 'border-slate-700 hover:border-slate-600'}`}
                    data-testid="method-bank-transfer">
                    <Landmark className={`w-8 h-8 mx-auto mb-2 ${paymentMethod === 'bank_transfer' ? 'text-primary' : 'text-slate-400'}`} />
                    <p className={`font-medium ${paymentMethod === 'bank_transfer' ? 'text-primary' : 'text-white'}`}>Bank Transfer</p>
                    <p className="text-slate-500 text-xs mt-1">Direct to company account</p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAcceptModal(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleAcceptQuote} className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={accepting || !paymentMethod} data-testid="confirm-accept-btn">
                {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {accepting ? "Processing..." : "Accept & Generate Invoice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Modal */}
        <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Receipt className="w-5 h-5 text-primary" />
                Invoice {invoiceRequest?.invoice?.invoice_id}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {invoiceRequest?.service_type?.replace(/_/g, ' ')}
              </DialogDescription>
            </DialogHeader>
            {invoiceRequest?.invoice && (
              <div className="space-y-4">
                {/* Total */}
                <div className="p-4 rounded-xl bg-slate-800 text-center">
                  <p className="text-slate-400 text-sm">Total Amount</p>
                  <p className="text-3xl font-bold text-white mono">{formatNaira(invoiceRequest.invoice.total)}</p>
                </div>

                {/* Installments */}
                <div className="space-y-2">
                  {invoiceRequest.invoice.installments.map((inst, idx) => (
                    <div key={`inv-inst-${idx}`} className="p-4 rounded-xl border border-slate-700 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{inst.label}</p>
                        <p className="text-slate-400 mono">{formatNaira(inst.amount)}</p>
                      </div>
                      {inst.status === "paid" ? (
                        <Badge className="status-complete"><CheckCircle className="w-3 h-3 mr-1" /> Paid</Badge>
                      ) : invoiceRequest.payment_method === "paystack" ? (
                        <Button size="sm" className="btn-primary" onClick={() => handlePayInstallment(invoiceRequest.request_id, idx)} disabled={paying}
                          data-testid={`pay-installment-${idx}`}>
                          {paying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                          Pay Now
                        </Button>
                      ) : (
                        <Badge className="status-pending"><Clock className="w-3 h-3 mr-1" /> Awaiting Transfer</Badge>
                      )}
                    </div>
                  ))}
                </div>

                {/* Bank Transfer Details */}
                {invoiceRequest.payment_method === "bank_transfer" && invoiceRequest.invoice.installments.some(i => i.status === "pending") && (
                  <div className="p-4 rounded-xl bg-slate-800 border border-primary/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Landmark className="w-5 h-5 text-primary" />
                      <p className="text-white font-medium">Bank Transfer Details</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Bank</span>
                        <span className="text-white font-medium">{BANK_DETAILS.bank}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Account Number</span>
                        <span className="text-white font-mono font-medium">{BANK_DETAILS.accountNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Account Name</span>
                        <span className="text-white font-medium">{BANK_DETAILS.accountName}</span>
                      </div>
                    </div>
                    <p className="text-amber-400 text-xs mt-3">
                      After making the transfer, your payment will be confirmed by our admin team.
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvoiceModal(false)} className="btn-secondary">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Decline Confirmation */}
        <AlertDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Decline Quote</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to decline the quote of {decliningRequest && formatNaira(decliningRequest.quote)}? You can request a new quote later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeclineQuote} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-decline-btn">
                Decline Quote
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default ServicesPage;
